import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'
import { mapTeamAvailability, type TeamAvailability } from '@/features/presence/api'

/** Dias na ordem em que a tela mostra (segunda primeiro); o valor é o que a Api aceita. */
export const WEEK_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

export type WeekDay = (typeof WEEK_DAYS)[number]

export interface ServiceWindow {
  readonly day: WeekDay
  /** HH:mm (hora local do fuso da política). */
  readonly opens: string
  readonly closes: string
}

export interface HandoffPolicy {
  readonly businessHoursEnabled: boolean
  readonly timeZoneId: string
  readonly schedule: readonly ServiceWindow[]
  readonly availableMessage: string | null
  readonly unavailableMessage: string | null
  readonly notifyTeamByEmail: boolean
  readonly defaultAvailableMessage: string
  readonly defaultUnavailableMessage: string
  readonly team: TeamAvailability
}

export type HandoffPolicyInput = Omit<
  HandoffPolicy,
  'defaultAvailableMessage' | 'defaultUnavailableMessage' | 'team'
>

/** Fusos oferecidos na tela (Brasil). O valor é o id IANA que a Api valida. */
export const TIME_ZONES = [
  'America/Sao_Paulo',
  'America/Bahia',
  'America/Fortaleza',
  'America/Recife',
  'America/Belem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Rio_Branco',
  'America/Noronha',
] as const

function isWeekDay(value: string): value is WeekDay {
  return (WEEK_DAYS as readonly string[]).includes(value)
}

function mapPolicy(dto: components['schemas']['HandoffPolicyResponse']): HandoffPolicy {
  return {
    businessHoursEnabled: dto.businessHoursEnabled,
    timeZoneId: dto.timeZoneId,
    schedule: dto.schedule
      .filter((window) => isWeekDay(window.day))
      .map((window) => ({
        day: window.day as WeekDay,
        opens: window.opens,
        closes: window.closes,
      })),
    availableMessage: dto.availableMessage,
    unavailableMessage: dto.unavailableMessage,
    notifyTeamByEmail: dto.notifyTeamByEmail,
    defaultAvailableMessage: dto.defaultAvailableMessage,
    defaultUnavailableMessage: dto.defaultUnavailableMessage,
    team: mapTeamAvailability(dto.team),
  }
}

async function getHandoffPolicy(tenantId: string): Promise<HandoffPolicy> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/handoff-policy', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok || result.data === undefined) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  return mapPolicy(result.data)
}

async function updateHandoffPolicy(
  tenantId: string,
  input: HandoffPolicyInput,
): Promise<HandoffPolicy> {
  const result = await apiClient.PUT('/api/v1/tenants/{tenantId}/handoff-policy', {
    params: { path: { tenantId } },
    body: {
      businessHoursEnabled: input.businessHoursEnabled,
      timeZoneId: input.timeZoneId,
      schedule: input.schedule.map((window) => ({ ...window })),
      availableMessage: input.availableMessage,
      unavailableMessage: input.unavailableMessage,
      notifyTeamByEmail: input.notifyTeamByEmail,
    },
  })

  if (!result.response.ok || result.data === undefined) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  return mapPolicy(result.data)
}

function handoffPolicyQueryKey(tenantId: string) {
  return ['handoff-policy', tenantId] as const
}

export function useHandoffPolicy(tenantId: string) {
  return useQuery({
    queryKey: handoffPolicyQueryKey(tenantId),
    queryFn: () => getHandoffPolicy(tenantId),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.problem.status === 403) && failureCount < 2,
  })
}

export function useUpdateHandoffPolicy(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: HandoffPolicyInput) => updateHandoffPolicy(tenantId, input),
    onSuccess: async (policy) => {
      queryClient.setQueryData(handoffPolicyQueryKey(tenantId), policy)
      // O status da equipe no topo muda com o horário.
      await queryClient.invalidateQueries({ queryKey: ['presence', tenantId] })
    },
  })
}
